import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import {
  alpha,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  Stack,
  Typography,
  type Theme,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { type DrawerContentConfig } from '../../../../@types';
import { getAccountDrawerConfig } from '../../../../config/accountDrawer';
import useLocalization from '../../../../hooks/useLocalization';
import useProductAvailability from '../../../../hooks/useProductAvailability';
import { SKIN } from '../../../../types/enums';
import { mergeSx } from '../../../../utils/styles';

const createStyles = (theme: Theme) =>
  ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      px: 1.5,
      pb: 1.5,
      pt: 0.5,
    },

    homeSurface: {
      borderRadius: 3,
      overflow: 'hidden',
      backgroundColor: 'neutral.600',
    },

    homeButton: {
      px: 1.5,
      py: 1.125,
      gap: 1,
      '&:hover': {
        backgroundColor: 'neutral.500',
      },
    },

    homeIcon: {
      minWidth: 30,
      color: 'neutral.300',
    },

    homeText: {
      color: 'neutral.100',
      typography: { xs: 'h5', md: 'h4' },
    },

    itemSurface: {
      overflow: 'hidden',
      borderRadius: 3,
      backgroundColor: 'neutral.600',
      '&:first-of-type': {
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
      },
      '&:last-of-type': {
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
      },
      '&::before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: 0,
      },
    },

    activeItemSurface: {
      border: '1px solid',
      borderColor: 'info.main',
    },

    itemSummary: {
      px: 1.5,
      py: 0.125,
      minHeight: 50,
      '& .MuiAccordionSummary-content': {
        alignItems: 'center',
        gap: 1,
        my: 0.875,
      },
      '& .MuiAccordionSummary-expandIconWrapper': {
        color: 'text.disabled',
      },
    },

    itemSummaryNoChildren: {
      '& .MuiAccordionSummary-content': {
        mr: 0,
      },
    },

    itemIcon: {
      minWidth: 28,
      color: 'secondary.200',
      '& .MuiSvgIcon-root': {
        fontSize: 20,
      },
    },

    itemText: {
      color: 'neutral.100',
      typography: 'body2',
      fontWeight: 700,
    },

    itemChildren: {
      display: 'flex',
      flexDirection: 'column',
      px: 1.25,
      pb: 1,
      pt: 0.25,
      gap: 0.625,
    },

    childButton: {
      justifyContent: 'space-between',
      px: 1.125,
      py: 0.875,
      borderRadius: 2,
      backgroundColor: 'neutral.700',
      color: 'text.secondary',
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.05),
      },
    },

    activeChildButton: {
      backgroundColor: 'neutral.500',
      color: 'neutral.50',
    },

    childText: {
      typography: 'body3',
      fontWeight: 700,
    },

    childDivider: {
      borderColor: alpha(theme.palette.common.white, 0.04),
    },

    divider: {
      my: 1,
    },

    list: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
  }) as const;

const isChildActive = (pathname: string, childPath: string) => {
  const pattern = `${childPath}/*`;

  return pathname === childPath || Boolean(matchPath({ path: pattern }, pathname));
};

type ItemProps = {
  item: DrawerContentConfig;
  pathname: string;
  styles: ReturnType<typeof createStyles>;
  navigateTo: (path: string) => void;
};

const AccountItem = ({ item, pathname, styles, navigateTo }: ItemProps) => {
  const [expanded, setExpanded] = useState(false);

  const hasActiveChild = useMemo(
    () => item.children.some((child) => isChildActive(pathname, child.path)),
    [item, pathname]
  );

  useEffect(() => {
    setExpanded(hasActiveChild);
  }, [hasActiveChild]);

  const isActive = item.path ? pathname === item.path || hasActiveChild : hasActiveChild;
  const itemTestId = `neobet-account-sidebar-item-${item.id}`;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, value) => {
        if (item.children.length) {
          setExpanded(value);
        }
      }}
      sx={mergeSx(styles.itemSurface, isActive && styles.activeItemSurface)}
      data-testid={itemTestId}
    >
      <AccordionSummary
        expandIcon={!!item.children.length && <ChevronRightRoundedIcon fontSize='small' />}
        sx={mergeSx(styles.itemSummary, !item.children.length && styles.itemSummaryNoChildren)}
        onClick={() => {
          if (item.path) {
            navigateTo(item.path);
          }
        }}
      >
        <ListItemIcon sx={styles.itemIcon}>{item.icon}</ListItemIcon>
        <Typography sx={styles.itemText}>{item.text}</Typography>
      </AccordionSummary>

      {!!item.children.length && (
        <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 0.75 }}>
          <Stack sx={styles.itemChildren}>
            {item.children.map((child, index) => {
              const childActive = isChildActive(pathname, child.path);

              return (
                <Fragment key={child.path}>
                  <ListItemButton
                    disableRipple
                    sx={mergeSx(styles.childButton, childActive && styles.activeChildButton)}
                    onClick={() => navigateTo(child.path)}
                  >
                    <Typography sx={styles.childText}>{child.text}</Typography>
                    <ChevronRightRoundedIcon fontSize='small' />
                  </ListItemButton>
                  {index !== item.children.length - 1 && <Divider sx={styles.childDivider} />}
                </Fragment>
              );
            })}
          </Stack>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

const NeobetAccountDrawerContent = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const { t } = useLocalization();
  const { isCasinoOnlyProduct, isSportsbookOnlyProduct } = useProductAvailability();
  const config = getAccountDrawerConfig(t, SKIN.NEOBET, { isCasinoOnlyProduct, isSportsbookOnlyProduct });
  const styles = createStyles(theme);

  return (
    <Stack sx={styles.root} data-testid='neobet-account-sidebar'>
      <Box sx={styles.homeSurface}>
        <ListItemButton
          disableRipple
          sx={styles.homeButton}
          onClick={() => navigate('/my-account')}
          data-testid='neobet-account-sidebar-home'
        >
          <ListItemIcon sx={styles.homeIcon}>
            <AccountCircleRoundedIcon />
          </ListItemIcon>
          <Typography sx={styles.homeText}>{t('myAccount', 'My Account')}</Typography>
        </ListItemButton>
      </Box>

      <Divider sx={styles.divider} />

      <List disablePadding sx={styles.list}>
        {config.map((item) => (
          <AccountItem key={item.id} item={item} pathname={pathname} styles={styles} navigateTo={navigate} />
        ))}
      </List>
    </Stack>
  );
};

export default NeobetAccountDrawerContent;
